import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormInput, FormSelect, FramerButton } from "@/components/shared";
import { handleTableCardTagColour } from "@/components/shared/table/utils";
import { DeployPageModals } from "@/constants/enums";
import { AssetFinancingPool, AssetFinancingPoolFragment, AssetFinancingStatus, AssetType } from "@/graphql/__generated__/graphql-operations";
import { useServiceProviderData } from "@/hooks";
import { useDeployAssetFinancingPoolContractMutationHook, useEditAssetFinancingPoolMutationHook, useAddAssetFinancingPoolMutationHook } from "@/hooks/mutations";
import { TAssetFinancingPoolSchema, AssetFinancingPoolSchema, AssetFinancingPoolDefaultValues } from "@/models";
import { calculateClosingDate } from "@/pages/Portolio/utils";
import { FormField } from "@/types";
import { onInvalid } from "@/utils/form";
import { convertBpToPercentage, formatDateLocale, formatDaysString, formatNumberLocale, toPascalCase } from "@/utils/helpers";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dispatch, JSX, SetStateAction, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

type Props = {
  showModal: DeployPageModals;
  currentSpId: string;
  setShowModal: Dispatch<SetStateAction<DeployPageModals>>;
}

/**
 * Modal component for displaying and editing AFP details of a Service Provider.
 *
 * @component
 * @example
 * ```tsx
 * import { ServiceProviderDetailsModal } from "@/pages/DeploySmartContracts/modals";
 *
 * const [showModal, setShowModal] = useState<DeployPageModals>(DeployPageModals.NoModal);
 *
 * return (
 *  {showModal === DeployPageModals.ServiceProviderDetailsModal && (
 *    <ServiceProviderDetailsModal
 *      showModal={showModal}
 *      setShowModal={setShowModal}
 *      currentSpId={currentSpId}
 *    />)
 *  }
 * )
 * ```
 *
 * @param {Object} props - The component props.
 * @param {DeployPageModals} props.showModal - The current modal state.
 * @param {Dispatch<SetState<DeployPageModals>>} props.setShowModal - Function to control the visibility of the modal.
 * @param {string} props.currentSpId - The ID of the current service provider.
 * @returns {JSX.Element} The rendered ServiceProviderDetailsModal component.
 */
function ServiceProviderDetailsModal(props: Props): JSX.Element {

  // Init form
  const {
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TAssetFinancingPoolSchema>({
    resolver: zodResolver(AssetFinancingPoolSchema as any),
    defaultValues: AssetFinancingPoolDefaultValues,
    mode: "onChange",
  });
  // Form constants
  const assetType = watch("assetType");

  // Props
  const { showModal, setShowModal, currentSpId } = props;

  // Hooks
  const intl = useIntl();
  const { t } = useTranslation();
  const spData = useServiceProviderData(currentSpId);

  // Mutation hooks
  const { deployMutation } = useDeployAssetFinancingPoolContractMutationHook();
  const { editAssetFinancingPoolMutation }  = useEditAssetFinancingPoolMutationHook();
  const { addAssetFinancingPoolMutation } = useAddAssetFinancingPoolMutationHook();

  // Constants
  const afp = spData?.assetfinancingpool;
  const isClosed = afp?.status === AssetFinancingStatus.Closed;
  const isOpen = afp?.status === AssetFinancingStatus.Open;
  const isOnboarding = afp?.status === AssetFinancingStatus.Onboarding;
  const isActive = afp?.status === AssetFinancingStatus.Active;
  const isReceivable = afp?.assetType === AssetType.Receivable;
  const poolAddress = afp?.pooladdress;

  // States
  const [editMode, setEditMode] = useState<boolean>(false);

  // Update form value when asset type changes
  useEffect(() => {
    if(currentSpId) {
      setValue("assetType", afp?.assetType as AssetType);
    }
  }, [currentSpId]);

  // Populate form with AFP data when AFP data is available
  useEffect(() => {
    if (afp) {
      // Use the defaultValues from formFields to populate the form
      formFields.forEach((field) => {
        if (field.defaultValue !== undefined) {
          // Check if field should be visible for current asset type
          const isFieldVisible = !field.visibility || field.visibility.includes(afp.assetType as AssetType);

          if (isFieldVisible) {
            // Special handling for uniqueitems dropdown which expects string but form expects boolean
            if (field.name === "uniqueitems") {
              setValue(field.name as any, afp.uniqueitems);
            } else {
              setValue(field.name as any, field.defaultValue);
            }
          } else {
            // Clear fields that shouldn't be visible for current asset type
            setValue(field.name as any, undefined);
          }
        }
      });
      setValue("assetType", afp.assetType as AssetType);
    }
  }, [afp, setValue]);

  // Clear Inventory-only fields when assetType changes to Receivable
  useEffect(() => {
    if (assetType === AssetType.Receivable) {
      setValue("ceiling", undefined);
      setValue("floor", undefined);
      setValue("uniqueitems", undefined);
    }
  }, [assetType, setValue]);

  /**
   * OnClick handler for Deploying smart contract
   *
   * @param {AssetFinancingPoolFragment} afp
   */
  async function handleOnDeployClick(afp: AssetFinancingPoolFragment) {
    // Prevent Deployment until AFP details has been updated in the DB
    if(afp.pooladdress !== "0x0" && afp.status === AssetFinancingStatus.Closed) {
      return null;
    }

    if(currentSpId !== "" ){
      toast.loading(t("toasts:deployLoadingToast"), {toastId: "deployLoadingToast"});
      await deployMutation(afp._id, currentSpId);
    }
    toast.dismiss("deployLoadingToast");
  }

  /**
   * OnSubmit handler for form
   * @param {TAssetFinancingPoolSchema} data
   */
  const onSubmit: SubmitHandler<TAssetFinancingPoolSchema> = (data: TAssetFinancingPoolSchema) => {
    toast.loading("Submitting data", {toastId: "editLoadingToast"});

    // Remove Inventory only fields incase user has changed type
    if(isReceivable) {
      setValue("ceiling", undefined);
      setValue("floor", undefined);
      setValue("uniqueitems", undefined);
    }

    try {
      // If AFC is closed, add new AFC
      if (isClosed) {
        addAssetFinancingPoolMutation({
          variables: {
            id: currentSpId,
            assetFinancingPoolInput: data
          }
        });
      }
      // Edit existing AFC if it is onboarding
      else {
        editAssetFinancingPoolMutation({
          variables: {
            id: currentSpId,
            assetFinancingPoolInput: data
          }
        });
      }
    }
    catch (error) {
      console.log("Error creating company", error);
    }
    finally {
      // Close loading toast and set edit mode to false
      toast.dismiss("editLoadingToast");
      setEditMode(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setShowModal(DeployPageModals.NoModal);
    }
  };

  const dateFields = [
    {
      label: t("deployafp.labels.amountinvested"),
      value: formatNumberLocale(intl, afp?.amountinvested as number, "currency"),
    },
    {
      label: "Activation date",
      value: isOpen || isOnboarding ? "Not available": formatDateLocale(intl, afp?.activationdate ?? null),
    },
    {
      label: "Closing date",
      value: calculateClosingDate(intl, afp?.activationdate, afp?.dayslocked as number),
    },
  ];

  const formFields: FormField[] = [
    {
      key: "asset-financing-pool-input-financing-limit",
      name: "financinglimit",
      type: "number",
      title: "Financing limit",
      placeholder: "Financing limit",
      required: true,
      min: 0,
      displayValue: formatNumberLocale(intl, afp?.financinglimit as number, "currency"),
      defaultValue: afp?.financinglimit,
    },
    {
      key: "asset-financing-pool-input-contract-cid",
      name: "contractCID",
      type: "text",
      title: "Contract CID",
      placeholder: "Contract CID",
      required: false,
      className: "md:min-w-[150px]",
      displayValue: afp?.contractCID ?? "Not available",
      defaultValue: afp?.contractCID ?? "",
    },
    {
      key: "asset-financing-pool-input-unique-items",
      name: "uniqueitems",
      type: "text",
      title: "Unique items",
      placeholder: "Unique items",
      isDropdown: true,
      required: assetType === AssetType.Inventory ? true : false,
      dropdownOptions: [
        { text: "Yes", value: "true" },
        { text: "No", value: "false" }
      ],
      visibility: [AssetType.Inventory],
      displayValue: afp?.uniqueitems ? "Yes" : "No",
      defaultValue: afp?.uniqueitems ? "true" : "false",
    },
    {
      key: "asset-financing-pool-input-ceiling",
      name: "ceiling",
      type: "number",
      title: "Ceiling (BPS)",
      placeholder: "Ceiling (BPS)",
      required: assetType === AssetType.Inventory ? true : false,
      min: 0,
      visibility: [AssetType.Inventory],
      displayValue: formatNumberLocale(intl, convertBpToPercentage(afp?.ceiling ?? 0), "percent"),
      defaultValue: afp?.ceiling ?? "",
    },
    {
      key: "asset-financing-pool-input-floor",
      name: "floor",
      type: "number",
      title: "Floor (BPS)",
      placeholder: "Floor (BPS)",
      required: assetType === AssetType.Inventory ? true : false,
      min: 0,
      visibility: [AssetType.Inventory],
      displayValue: formatNumberLocale(intl, convertBpToPercentage(afp?.floor ?? 0), "percent"),
      defaultValue: afp?.floor ?? "",
    },
    {
      key: "asset-financing-pool-input-interest",
      name: "interest",
      type: "number",
      title: "Interest (BPS)",
      placeholder: "Interest (BPS)",
      required: false,
      min: 0,
      displayValue: (convertBpToPercentage(afp?.interest ?? 0)*100).toFixed(2)+"%",
      defaultValue: afp?.interest ?? "",
    },
    {
      key: "asset-financing-pool-input-payout-period",
      name: "rewardpayoutperiod",
      type: "number",
      title: "Reward payout period",
      placeholder: "Reward payout period",
      required: false,
      min: 1,
      displayValue: `Every ${afp?.rewardpayoutperiod} days`,
      defaultValue: afp?.rewardpayoutperiod ?? "",
    },
    {
      key: "asset-financing-pool-input-days-locked",
      name: "dayslocked",
      type: "number",
      title: "Days locked",
      placeholder: "Days locked",
      required: false,
      min: 1,
      displayValue: formatDaysString(afp?.dayslocked as number),
      defaultValue: afp?.dayslocked ?? "",
    },
  ];

  return (
    <Dialog open={showModal === DeployPageModals.ServiceProviderDetails} onOpenChange={handleOpenChange}>
      <DialogContent className="px-6 min-w-2xl max-w-3xl max-h-[90vh] overflow-y-auto dark:bg-mfdarklight"
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold dark:text-white">{spData?.name}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 pt-2 overflow-y-auto">
          <div className="md: max-h-[30%]">
            {/* Top container */}
            <div className="flex items-center justify-between -mt-2">
              {/* Tags */}
              <div className="flex items-center gap-2">
                <div className={`rounded-md text-sm border-[1px] p-1 select-none ${handleTableCardTagColour(afp?.status as string, "Company")}`}>
                  {toPascalCase(afp?.status as string)}
                </div>
                <div className={`rounded-md text-sm border-[1px] p-1 select-none ${handleTableCardTagColour(afp?.assetType as string, "")}`}>
                  {toPascalCase(afp?.assetType as string)}
                </div>
              </div>
            </div>

            {/* Addresses */}
            <div className="my-2">
              <div>
                <p className="text-sm dark:text-gray-400">Pool Address</p>
                <p className="break-all">{afp?.pooladdress}</p>
              </div>
              <div>
                <p className="text-sm dark:text-gray-400">Token Address</p>
                <p className="break-all">{afp?.tokenaddress}</p>
              </div>
            </div>

            {/* AFP Details Form */}
            <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-2">
              {
              // Action buttons
                <div className="flex space-x-2 justify-end mb-4">
                  {editMode ? (
                    <>
                      {/* Cancel button */}
                      <FramerButton
                        type="button"
                        onClick={() => setEditMode(false)}
                        variant="outline"
                        className="border-mforange text-mforange bg-transparent"
                      >
                        Cancel
                      </FramerButton>
                      {/* Submit button */}
                      <FramerButton
                        type="submit"
                        className="bg-mforange text-white hover:bg-mforange/80"
                      >
                        Submit
                      </FramerButton>
                    </>
                  ) : (
                  // Edit Button
                    <FramerButton
                      type="button"
                      onClick={() => setEditMode(true)}
                      // Button is disabled if AFP is ACTIVE or OPEN
                      disabled={isActive || isOpen}
                      className={`${isActive || isOpen ? "opacity-50 cursor-not-allowed bg-gray-400" : "bg-mforange text-white hover:bg-mforange/80"}`}
                    >
                      Edit
                    </FramerButton>
                  )}
                </div>
              }
              {dateFields.map((field) => (
                <div key={`spInfoField-${field.label}`} className="flex items-center justify-between">
                  <p className="text-sm font-semibold pr-2 text-gray-800 dark:text-gray-100">{field.label}</p>
                  <p className="text-sm text-gray-800 dark:text-gray-100">{field.value}</p>
                </div>
              ))}

              {/* Input fields OR text info */}
              <div className={`${editMode ? "md:flex md:flex-wrap md:gap-x-4" : "space-y-2"} p-1`}>
                {formFields.map((field) => {
                  const isFieldVisible = !field.visibility || field.visibility.includes(afp?.assetType as AssetType);
                  return (
                    isFieldVisible && (
                      <div key={`sp-info-field-${field.title}`} className={`${editMode ? "max-md:m∂in-w-full md:min-w-[48%] max-w-min" : ""}`}>
                        {editMode ? (
                          <div className="w-full">
                            {field.isDropdown ? (
                              <FormSelect
                                key={field.key}
                                register={register as any}
                                errors={errors}
                                field={field}
                                watch={watch as any}
                                setValue={setValue as any}
                              />
                            ) : (
                              <FormInput
                                key={field.key}
                                register={register as any}
                                errors={errors}
                                field={field}
                                watch={watch as any}
                              />
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-between w-full">
                            <p className="text-sm font-semibold pr-2 text-gray-800 dark:text-gray-100">{field.title}</p>
                            <p className="text-sm text-gray-800 dark:text-gray-100">{field.displayValue}</p>
                          </div>
                        )}
                      </div>
                    )
                  );
                })}
              </div>
            </form>
          </div>
        </div>

        <DialogFooter>
          {
            // Only show button if AFP Status is Closed or Onboarding
            // Hide button during edit mode
            (isClosed || isOnboarding) && !editMode &&
              // Action buttons
              <div className="flex w-full items-center justify-center mt-4">
                <FramerButton
                  disabled={isClosed && poolAddress !== "0x0"}
                  onClick={() => handleOnDeployClick(afp as AssetFinancingPool)}
                  className={`${isClosed && poolAddress !== "0x0" ? "opacity-50 cursor-not-allowed bg-gray-400" : "bg-mforange text-white hover:bg-mforange/80"}`}
                >
                  {"Deploy"}
                </FramerButton>
              </div>
          }
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ServiceProviderDetailsModal;
